/**
 * Vendor Bot Admin - Product Form
 * Add/edit product (physical/digital/keys/subscription)
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const Label = styled.label`
  font-size: var(--font-md);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }
`;

const Textarea = styled.textarea`
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }
`;

const Select = styled.select`
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--spacing-md);

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }
`;

const HelpText = styled.p`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
`;

const Switch = styled.input.attrs({ type: 'checkbox' })`
  width: 48px;
  height: 28px;
  appearance: none;
  background: var(--border-color);
  border-radius: 14px;
  position: relative;
  cursor: pointer;
  transition: background var(--transition);

  &:checked {
    background: var(--accent-green);
  }

  &::before {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: 2px;
    transition: transform var(--transition);
  }

  &:checked::before {
    transform: translateX(20px);
  }
`;

const ProductForm: React.FC = () => {
  const { botUsername, id } = useParams<{ botUsername: string; id: string }>();
  const navigate = useNavigate();
  const { showMainButton, showBackButton, notificationFeedback } = useTelegram();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    type: 'physical',
    stock: '',
    active: true,
  });

  const isNew = id === 'new';

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/products`));
  }, [showBackButton, navigate, botUsername]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    setTimeout(() => {
      notificationFeedback('success');
      setLoading(false);
      navigate(`/vendor/${botUsername}/admin/products`);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const isFormValid = formData.name.trim() !== '' && formData.price !== '';

  useEffect(() => {
    if (isFormValid) {
      showMainButton(isNew ? 'Create Product' : 'Save Changes', () => {
        const form = document.getElementById('product-form') as HTMLFormElement;
        form?.requestSubmit();
      });
    }
  }, [isFormValid, showMainButton, isNew]);

  return (
    <Layout header={<Navbar title={isNew ? 'Add Product' : 'Edit Product'} showBack />}>
      <Form id="product-form" onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="type">Product Type *</Label>
          <Select id="type" name="type" value={formData.type} onChange={handleChange}>
            <option value="physical">📦 Physical Product</option>
            <option value="digital">💾 Digital Product</option>
            <option value="keys">🔑 Product Keys</option>
            <option value="subscription">🔄 Subscription</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="name">Product Name *</Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter product name"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your product..."
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="price">Price (USD) *</Label>
          <Input
            id="price"
            name="price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={handleChange}
            placeholder="0.00"
            required
          />
        </FormGroup>

        {formData.type === 'physical' && (
          <FormGroup>
            <Label htmlFor="stock">Stock Quantity</Label>
            <Input
              id="stock"
              name="stock"
              type="number"
              value={formData.stock}
              onChange={handleChange}
              placeholder="0"
            />
            <HelpText>Leave empty for unlimited stock</HelpText>
          </FormGroup>
        )}

        <SwitchContainer>
          <Label htmlFor="active">Product Active</Label>
          <Switch id="active" name="active" checked={formData.active} onChange={handleChange} />
        </SwitchContainer>

        <Button type="submit" loading={loading} disabled={!isFormValid} fullWidth>
          {isNew ? 'Create Product' : 'Save Changes'}
        </Button>

        {!isNew && (
          <Button type="button" variant="danger" fullWidth>
            Delete Product
          </Button>
        )}
      </Form>
    </Layout>
  );
};

export default ProductForm;
